/**
 * Extend Brand Colors (Source of Truth)
 *
 * Two-tier architecture matching Figma variables:
 * 1. baseColors - Raw hex values (blue300, grey800, etc.)
 * 2. semanticColors - References to baseColors OR other semanticColors
 *
 * Naming convention enforces hierarchy:
 * - base_*, gradient_*, logo_*, overlay_*, tag_* → must reference baseColors only
 * - cta_*, input_*, nav_*, table_*, text_*, notification_*, graphics_*, charts_* → can reference base or semantic
 *
 * Brands can override any tier:
 * - baseColors: Change hex values
 * - semanticColors: Point to different base or semantic colors
 */

// =============================================================================
// BASE COLORS - Raw hex values
// =============================================================================

export const extendBaseColors = {
  // Blue palette
  blue100: '#EBF2FE',
  blue150: '#D0E1FD',
  blue200: '#619BF9',
  blue300: '#4489F9',
  blue400: '#3475F2',
  blue450: '#2750A0',
  blue500: '#1B2C4F',

  // Green palette
  green30: '#C8EDE5',
  green50: '#B4EFE3',
  green100: '#82DCC9',
  green150: '#5FCEB6',
  green200: '#14C8B4',
  green300: '#23B998',
  green400: '#009183',
  green500: '#006E65',
  green600: '#004A44',

  // Grey palette
  grey50: '#FCFCFF',
  grey100: '#FCFCFF',
  grey150: '#F9F9FD',
  grey200: '#F6F7FB',
  grey300: '#F0F1F6',
  grey400: '#EAEBF1',
  grey450: '#DADBE0',
  grey500: '#B3B7BE',
  grey700: '#697080',
  grey800: '#000000',

  // Purple palette
  purple100: '#F6F7FB',
  purple200: '#EFEEFE',
  purple250: '#D3CFF7',
  purple300: '#B1A9F8',
  purple400: '#9C92FB',
  purple500: '#4F3FDD',
  purple600: '#3B2DBD',

  // Red palette
  red100: '#FAD3D6',
  red200: '#F49FA7',
  red300: '#D5404A',
  red400: '#C11622',
  red500: '#A0121B',
  red600: '#450A0A',

  // White
  white100: '#FFFFFF',

  // Yellow palette
  yellow50: '#FFF4CF',
  yellow100: '#FFE1AF',
  yellow200: '#F4C72A',
  yellow800: '#856700',
  yellow900: '#5C4700',

  // Neutral
  neutral600: '#6F6C90',

  // Data visualization palette
  dataviz1: '#7E88CE',
  dataviz2: '#5BA99D',
  dataviz3: '#A38CC8',
  dataviz4: '#7BA7DD',
  dataviz5: '#C6A568',
  dataviz6: '#BC8163',
  datavizneutral: '#C7CCD6',

  // Avatar fallback palette
  avatarFallback01: '#A5ACDD',
  avatarFallback02: '#CEE5E2',
  avatarFallback03: '#94C7BF',
  avatarFallback04: '#5BA99D',
  avatarFallback05: '#A38CC8',
  avatarFallback06: '#B0CAEB',
  avatarFallback07: '#7BA7DD',
  avatarFallback08: '#DDC9A4',
  avatarFallback09: '#C6A568',
  avatarFallback10: '#EBD9D0',
  avatarFallback11: '#D7B3A1',
  avatarFallback12: '#C38E73',
  avatarFallbackText: '#000000',

  // Marketing
  marketingGreen100: '#E4FFF9',
  marketingGreen400: '#21BA99',

  // Misc
  progressBarBg: '#d9d9d9',
} as const

export type BaseColors = typeof extendBaseColors
export type BaseColorKey = keyof BaseColors

// =============================================================================
// SEMANTIC COLORS - References to base colors OR other semantic colors
//
// Naming convention:
// - base_*, gradient_*, logo_*, overlay_*, tag_* prefixes → reference baseColors only
// - Other prefixes (cta_*, input_*, etc.) → can reference baseColors or semanticColors
// =============================================================================

export const extendSemanticColors = {
  // ---------------------------------------------------------------------------
  // Core semantic colors (must reference baseColors only)
  // ---------------------------------------------------------------------------

  // Base semantic colors
  base_colorAction: 'blue300',
  base_colorActionHover: 'blue400',
  base_colorNew: 'purple500',
  base_colorAlert: 'red300',
  base_colorAlertHover: 'red400',
  base_contentBackground: 'white100',
  base_contentActive: 'blue100',
  base_contentHover: 'grey200',
  base_colorSurface: 'grey200',
  base_colorSurfaceSecondary: 'grey50',
  base_colorSurfaceMuted: 'grey150',
  base_colorSurfaceMutedHover: 'grey300',
  base_colorRaisedSurface: 'white100',
  base_colorContentBorder: 'grey450',
  base_colorContentBorderSecondary: 'grey200',
  base_colorGradientStart: 'purple300',
  base_colorGradientEnd: 'purple200',
  base_colorHighlight: 'purple500',

  // Gradient semantic colors
  gradient_brandGradientStart: 'green150',
  gradient_brandGradientEnd: 'blue200',
  gradient_barChartGradientStart: 'purple300',
  gradient_barChartGradientEnd: 'purple200',
  gradient_selectedGradientStart: 'purple500',
  gradient_selectedGradientEnd: 'purple400',
  gradient_marketingGradientStart: 'marketingGreen100',
  gradient_marketingGradientEnd: 'white100',

  // Logo semantic colors
  logo_colorLogoPrimary: 'green400',
  logo_colorLogoSecondary: 'green200',

  // Overlay semantic colors
  overlay_bgColor: 'blue500',

  // Tag - Highlight
  tag_colorTagHighlightBackground: 'green50',
  tag_colorTagHighlightText: 'green500',
  tag_colorTagHighlightBackgroundHover: 'green150',
  tag_colorTagHighlightTextHover: 'green600',

  // Tag - Default
  tag_colorTagDefaultBackground: 'grey400',
  tag_colorTagDefaultText: 'grey800',
  tag_colorTagDefaultBackgroundHover: 'grey500',
  tag_colorTagDefaultTextHover: 'grey800',

  // Tag - Severe
  tag_colorTagSevereBackground: 'red100',
  tag_colorTagSevereText: 'red400',
  tag_colorTagSevereBackgroundHover: 'red200',
  tag_colorTagSevereTextHover: 'red500',

  // Tag - Pending
  tag_colorTagPendingBackground: 'yellow50',
  tag_colorTagPendingText: 'yellow800',
  tag_colorTagPendingBackgroundHover: 'yellow100',
  tag_colorTagPendingTextHover: 'yellow900',

  // Tag - New
  tag_colorTagNewBackground: 'purple200',
  tag_colorTagNewText: 'purple500',
  tag_colorTagNewBackgroundHover: 'purple250',
  tag_colorTagNewTextHover: 'purple600',

  // Tag - Blue
  tag_colorTagBlueBackground: 'blue100',
  tag_colorTagBlueText: 'blue400',
  tag_colorTagBlueBackgroundHover: 'blue150',
  tag_colorTagBlueTextHover: 'blue500',

  // ---------------------------------------------------------------------------
  // Component/derived colors (can reference baseColors OR semanticColors)
  // ---------------------------------------------------------------------------

  // AlertPanel colors
  alertPanel_defaultText: 'tag_colorTagBlueText',
  alertPanel_defaultBorder: 'tag_colorTagBlueText',
  alertPanel_warningText: 'tag_colorTagPendingText',
  alertPanel_warningBorder: 'yellow200',
  alertPanel_automationText: 'purple400',
  alertPanel_automationBorder: 'purple400',
  alertPanel_wcagText: 'text_colorTextSecondary',

  // Avatar colors
  avatar_colorSurface: 'grey150', // -> grey150 default
  avatar_colorFallback01: 'avatarFallback01',
  avatar_colorFallback02: 'avatarFallback02',
  avatar_colorFallback03: 'avatarFallback03',
  avatar_colorFallback04: 'avatarFallback04',
  avatar_colorFallback05: 'avatarFallback05',
  avatar_colorFallback06: 'avatarFallback06',
  avatar_colorFallback07: 'avatarFallback07',
  avatar_colorFallback08: 'avatarFallback08',
  avatar_colorFallback09: 'avatarFallback09',
  avatar_colorFallback10: 'avatarFallback10',
  avatar_colorFallback11: 'avatarFallback11',
  avatar_colorFallback12: 'avatarFallback12',
  avatar_colorFallbackText: 'avatarFallbackText',

  // Tooltip colors
  tooltip_colorDefaultSurface: 'blue500', // -> blue500 default
  tooltip_colorSpotlightSurface: 'purple600', // -> purple600 default (.spotlight variant)
  tooltip_colorBorder: 'grey150', // -> grey150 default

  // Box colors
  box_colorSelectedBorder: 'purple250', // -> purple250 default

  // Chart colors
  charts_barChartBillPayDefault: 'green300',
  charts_barChartBillPayDisabled: 'green30',
  charts_barChartVirtualCardsDefault: 'purple400',
  charts_barChartVirtualCardsDisabled: 'purple200',
  charts_barChartPhysicalCardsDefault: 'blue300',
  charts_barChartPhysicalCardsDisabled: 'blue150',
  charts_categorical1: 'dataviz1',
  charts_categorical2: 'dataviz2',
  charts_categorical3: 'dataviz3',
  charts_categorical4: 'dataviz4',
  charts_categorical5: 'dataviz5',
  charts_categorical6: 'dataviz6',
  charts_categoricalOther: 'datavizneutral',
  charts_cardTypePhysical: 'charts_categorical4',
  charts_cardTypeVirtual: 'charts_categorical3',
  charts_stackedBarSeriesOne: 'charts_categorical1',
  charts_stackedBarSeriesTwo: 'charts_categorical2',
  charts_stackedBarSeriesThree: 'charts_categorical3',
  charts_stackedBarSeriesFour: 'charts_categorical4',
  charts_stackedBarSeriesFive: 'charts_categorical5',
  charts_stackedBarSeriesSix: 'charts_categorical6',
  charts_stackedBarZeroLine: 'grey700',
  charts_stackedBarGridLine: 'grey450',
  charts_stackedBarHighlight: 'blue100',

  // CTA - Link colors
  cta_linkTextPrimary: 'base_colorAction',
  cta_linkTextPrimaryHover: 'base_colorActionHover',
  cta_linkTextAlert: 'red300',
  cta_linkTextAlertHover: 'red400',

  // CTA - Primary button
  cta_buttonPrimaryBackground: 'base_colorAction',
  cta_buttonPrimaryBackgroundHover: 'base_colorActionHover',
  cta_buttonPrimaryBorder: 'blue400',
  cta_buttonPrimaryText: 'white100',

  // CTA - Secondary button
  cta_buttonSecondaryBackground: 'grey50',
  cta_buttonSecondaryBackgroundHover: 'grey300',
  cta_buttonSecondaryBorder: 'base_colorContentBorder',
  cta_buttonSecondaryText: 'base_colorAction',

  // CTA - Alert/Severe button
  cta_alertButtonBackground: 'base_colorAlert',
  cta_alertButtonBackgroundHover: 'red400',
  cta_alertButtonBorder: 'red400',
  cta_alertButtonSecondaryBackground: 'grey50',
  cta_alertButtonSecondaryBackgroundHover: 'tag_colorTagSevereBackground',

  // CTA - Noshell button
  cta_noshellButtonBackgroundHover: 'grey400',
  cta_noshellButtonAnimatedCircle: 'grey200',

  // CTA - Spotlight button
  cta_spotlightButtonBackground: 'purple500',
  cta_spotlightBackgroundHover: 'purple600',
  cta_spotlightButtonBorder: 'purple600',

  // Input colors
  input_backgroundDefault: 'grey100',
  input_borderDefault: 'grey400',
  input_borderHover: 'grey500',
  input_borderActive: 'base_colorAction',
  input_borderError: 'base_colorAlert',

  // Navigation colors
  nav_navItemColorHover: 'base_contentActive',
  nav_navItemColorSelected: 'base_contentActive',
  nav_navAccordionColorSelected: 'grey200',

  // Table colors
  table_tableBorderColor: 'base_colorContentBorder',
  table_tableRowSelected: 'base_contentActive',
  table_tableRowSelectedHover: 'blue150',
  table_tableRowHover: 'base_contentHover',

  // Text colors
  text_colorTextPrimary: 'blue500',
  text_colorTextSecondary: 'grey700',
  text_colorTextTertiary: 'grey400',
  text_colorTextBranded: 'green300',
  text_colorTextAccent: 'green500',
  text_colorTextSuccess: 'green300',

  // Warning / feedback colors (intent-scoped; supersede #6912 alertPanel_* approach)
  warning_background: 'yellow50',
  warning_border: 'yellow200',
  warning_text: 'yellow800',

  // Notification colors
  notification_colorAlert: 'base_colorAlert',

  // Graphics colors
  graphics_illustrationGradientPrimaryStart: 'logo_colorLogoSecondary',
  graphics_illustrationFillSecondary: 'marketingGreen100',
  graphics_illustrationFillPrimary: 'marketingGreen400',
} as const

export type SemanticColors = typeof extendSemanticColors
export type SemanticColorKey = keyof SemanticColors

// =============================================================================
// RESOLVED COLORS TYPE - Final output after resolution
// =============================================================================

/** All color keys that can be exported */
export type AllColorKey = BaseColorKey | SemanticColorKey

/** Resolved colors: all tiers resolved to hex values */
export type ResolvedColors = {
  [K in AllColorKey]: string
}
